import { useQuery, QueryClient } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { useDriverProfile } from './useDriverProfile';
import { companyIdFromProfileOrStorage } from './userCompanies.shared';
import { QueryConfig } from '../api/client';
import { driverApi, DriverImpound } from '../services/driverApi';

const getDriverImpound = async (
  token: string,
  impoundId: string,
  companyId?: string
): Promise<DriverImpound> => {
  driverApi.setAuthToken(token);
  return driverApi.getImpound(impoundId, companyId);
};

export const prefetchDriverImpound = (
  queryClient: QueryClient,
  token: string,
  impoundId: string,
  companyId?: string
): Promise<void> =>
  queryClient.prefetchQuery({
    queryKey: ['driver-impound', impoundId],
    queryFn: () => getDriverImpound(token, impoundId, companyId),
    staleTime: 1 * 60 * 1000,
  });

export const useDriverImpound = (
  impoundId: string | undefined,
  config?: QueryConfig<typeof getDriverImpound>
) => {
  const { getAccessTokenSilently, isAuthenticated } = useHybridAuth();
  const { data: profile } = useDriverProfile();
  // An office member with no Driver row has a placeholder profile with no
  // company; the stored selection stands in, or the impound never loaded.
  const companyId = companyIdFromProfileOrStorage(profile);

  return useQuery({
    ...config,
    queryFn: async () => {
      if (!profile) throw new Error('Profile not loaded');
      const token = await getAccessTokenSilently();
      return getDriverImpound(token, impoundId!, companyId);
    },
    queryKey: ['driver-impound', impoundId],
    enabled:
      isAuthenticated &&
      !!impoundId &&
      !!companyId &&
      config?.enabled !== false,
    staleTime: 1 * 60 * 1000, // 1 minute for individual impound details
  });
};
